/**
 * A hint card for each check of the walk (W6 of docs/plans/setup-walk.md):
 * what it means, why it matters to the owner, its level, and its fix. The
 * fix names the safe change code may make, or the choice to ask; Phase 2
 * turns each into ops and the person's words into i18n keys. Until then the
 * cards are the team's: English, never shown in the app.
 *
 * Pure: the phone bundles it.
 */

/** `blocks`: the person cannot finish the task. `hint`: it works, but costs steps (W4). */
export type WalkLevel = 'blocks' | 'hint';

/** The general journeys (W2): take an entry, settle what it leaves owed, and see it counted. */
export type WalkJourney = 'take' | 'settle' | 'count';

export const WALK_CHECK_IDS = [
  'amount_unreachable',
  'no_price',
  'price_from_list',
  'price_typed_each_time',
  'choice_without_price',
  'dues_with_no_party',
  'no_settle_path',
  'lines_money_uncounted',
  'rollup_not_read',
  'paid_not_counted',
] as const;
export type WalkCheckId = (typeof WALK_CHECK_IDS)[number];

export type WalkCard = {
  /** The level the check raises at most; `price_from_list` raises either (W5). */
  readonly level: WalkLevel;
  readonly journey: WalkJourney;
  readonly meaning: string;
  readonly why: string;
  /** What code may change without asking: none for a check that rests on words (W7). */
  readonly safeFix: string | null;
  /** The choice put to the person when there is no safe fix, or as well as it. */
  readonly ask: string | null;
  /** Whether the check reads labels as well as shape: such a finding is never fixed alone (W7). */
  readonly words: boolean;
};

export const WALK_CARDS: Readonly<Record<WalkCheckId, WalkCard>> = {
  amount_unreachable: {
    level: 'blocks',
    journey: 'take',
    meaning: "The money rule's amount can never be worked out: a field it reads does not exist, or a pick reads a field the list does not hold.",
    why: 'Every entry saves with no amount, so nothing is counted and nobody owes anything.',
    safeFix: 'When one field is missing and the list holds a price under another name, read that one.',
    ask: 'Which field holds the price?',
    words: false,
  },
  no_price: {
    level: 'blocks',
    journey: 'take',
    meaning: 'A record that sells holds no money at all, and no other money record picks it.',
    why: 'The owner can take a booking but cannot say what it costs or what is owed.',
    safeFix: null,
    ask: 'Add a price and a total to it, or a record of its payments?',
    words: true,
  },
  price_from_list: {
    level: 'blocks',
    journey: 'take',
    meaning: 'The amount needs a field of a picked row, and some rows do not hold it.',
    why: 'An entry that picks such a row has no amount (blocks); a default that starts from it opens empty and costs typing (hint).',
    safeFix: 'Make the field required on the list.',
    ask: 'Type the missing value on each row named.',
    words: false,
  },
  price_typed_each_time: {
    level: 'hint',
    journey: 'take',
    meaning: 'The record picks a row that holds a price, and no field starts from it.',
    why: 'The price is typed on every entry although the row already knows it.',
    safeFix: 'Start the price field from the picked row, so the person may still type over it.',
    ask: null,
    words: true,
  },
  choice_without_price: {
    level: 'hint',
    journey: 'take',
    meaning: 'A type-like choice (Standard, Deluxe) on a sale, or on a list it picks, that no price follows.',
    why: 'The owner has to remember each type\'s price and type it on every entry.',
    safeFix: null,
    ask: 'Does each type have its own price?',
    words: true,
  },
  dues_with_no_party: {
    level: 'blocks',
    journey: 'settle',
    meaning: 'The money rule can leave money owed and names nobody who owes it.',
    why: 'What is not paid at once is owed by nobody, so it cannot be collected.',
    safeFix: null,
    ask: 'Who owes it: a customer picked on the entry, or the entry itself?',
    words: false,
  },
  no_settle_path: {
    level: 'blocks',
    journey: 'settle',
    meaning: 'A party can owe, and no record and no payment lines take the rest.',
    why: 'At check-out the owner has nowhere to record the rest of the money.',
    safeFix: 'Add a record of payments against that party.',
    ask: null,
    words: false,
  },
  lines_money_uncounted: {
    level: 'hint',
    journey: 'count',
    meaning: 'Lines hold money that no total and no money rule reads.',
    why: 'The owner types amounts that are never added up or counted.',
    safeFix: null,
    ask: 'Should these amounts add up into the total?',
    words: false,
  },
  rollup_not_read: {
    level: 'hint',
    journey: 'count',
    meaning: 'A total of other entries (the room bill) shows beside a balance that does not read it.',
    why: 'The balance at check-out leaves out what was put on the bill.',
    safeFix: null,
    ask: 'Should the balance include it?',
    words: true,
  },
  paid_not_counted: {
    level: 'blocks',
    journey: 'count',
    meaning: 'A bill keeps what was paid at once (Paid now, Advance, Dibayar), and its money rule never reads it.',
    why: 'A part payment counts as paid in full, or as nothing: Cash and what the customer owes come out wrong.',
    safeFix: 'Read it as paid at once, into the mode the bill names, and count it in the bill\'s Received and Balance.',
    ask: null,
    words: true,
  },
};
